import { subWeeks } from 'date-fns'
import { z } from 'zod'
import { Monotype } from '~/components/text'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { Icon, TableCell, TableRow, cn } from '@hap/components'
import { toPercent } from '@hap/utils'

import type { JSX } from "react";

export function MetricWidget(props: Props): JSX.Element | null {
  const filters: AnalyticsQueryFilter[] = [
    {
      field: 'scorecardCriteriaId',
      op: 'eq',
      value: props.id,
      dataset: 'evaluationCriteriaScores',
    },
    {
      field: 'publishedAt',
      op: 'isNotNull',
      dataset: 'evaluations',
    },
  ]

  if (props.filters) {
    if (props.filters.workspaceIds) {
      filters.push({
        field: 'workspaceId',
        ...props.filters.workspaceIds,
        dataset: 'interactions',
      })
    }
    if (props.filters.groupIds) {
      filters.push({
        field: 'groupIds',
        ...props.filters.groupIds,
        dataset: 'interactions',
      })
    }
    if (props.filters.reportToIds) {
      filters.push({
        field: 'assignedSupervisorId',
        ...props.filters.reportToIds,
        dataset: 'interactions',
      })
    }
    if (props.filters.handledBy) {
      filters.push({
        field: 'handledBy',
        ...props.filters.handledBy,
        dataset: 'interactions',
      })
    }
    if (props.filters.assignedTo) {
      filters.push({
        field: 'assignedTo',
        ...props.filters.assignedTo,
        dataset: 'evaluationCriteriaScores',
      })
    }
    if (props.filters.reviewed) {
      filters.push({
        field: 'reviewedAt',
        ...props.filters.reviewed,
        dataset: 'evaluations',
      })
    }
  }

  if (props.scorecardId) {
    filters.push({
      field: 'scorecardId',
      op: 'eq',
      value: props.scorecardId,
      dataset: 'evaluations',
    })
  }

  const { data } = useAnalytics({
    timespan: DEFAULT_TIMESPAN,
    dataset: 'evaluationCriteriaScores',
    filters: filters,
    aggregations: {
      avg: {
        field: 'score',
        op: 'avg',
        dataset: 'evaluationCriteriaScores',
      },
    },
  })

  // const DEFAULT_SERIES = 'week'
  // const { data: data2 } = useAnalytics({
  //   timespan: DEFAULT_TIMESPAN,
  //   dataset: 'evaluationCriteriaScores',
  //   filters: filters,
  //   aggregations: {
  //     avg: {
  //       field: 'score',
  //       op: 'avg',
  //       dataset: 'evaluationCriteriaScores',
  //     },
  //   },
  //   series: DEFAULT_SERIES,
  // })

  // const formattedData = useMemo(() => {
  //   return data2
  //     ? data2.map((d) => {
  //         const time = formatDateBySeries(d.time, DEFAULT_SERIES)
  //         const avg = d.avg ?? 0
  //         return {
  //           time,
  //           avg: avg,
  //           // boundary: [d.upper ?? avg, d.lower ?? avg],
  //         }
  //       })
  //     : createPlaceholderData(DEFAULT_TIMESPAN, DEFAULT_SERIES)
  // }, [data2])

  const score = data ? parseFloat(data[0].avg) : null
  const belowGoal = score && score < 0.8
  if (props.scope === 'Below' && !belowGoal) {
    return null
  }

  if (props.scope === 'Above' && (belowGoal || score === null || isNaN(score))) {
    return null
  }
  return (
    // <div
    //   className={cn('flex flex-col justify-between rounded-md border p-4', {
    //     'border-warn text-warn': data && parseFloat(data[0].avg) < 0.8,
    //   })}
    // >
    //   <header className="shrink grow">
    //     <h3 className="mb-2 font-semibold">{props.name}</h3>
    //   </header>
    //   <div className="flex shrink-0 gap-4">
    //     <div className="text-2xl font-semibold leading-tight">
    //     </div>
    //     <LinkButton to={`./${props.id}`} variant="secondary" size="icon-sm">
    //       <Icon name="arrow-right" />
    //     </LinkButton>
    //   </div>
    // </div>
    <TableRow>
      <TableCell className={cn('font-bold', { 'text-warn': belowGoal || false })}>
        {belowGoal ? (
          <Icon name="warning-diamond" className="stroke-warn mr-1 inline" />
        ) : null}

        {props.name}
      </TableCell>
      {/* {formattedData.map((d, i) => {
        return (
          <TableCell key={i} align="right" className="max-w-[100px]">
            <Monotype>{toPercent(d.avg)}</Monotype>
          </TableCell>
        )
      })} */}
      <TableCell align="right" className="max-w-[100px]">
        <Monotype>{toPercent(score)}</Monotype>
      </TableCell>
    </TableRow>
  );
}

export interface MetricFilters {
  workspaceIds?: { op: 'in'; values: string[] }
  groupIds?: { op: 'in'; values: string[] }
  channelIds?: { op: 'in'; values: string[] }
  reportToIds?: { op: 'in'; values: string[] }
  handledBy?: { op: 'in'; values: string[] }
  scorecardIds?: { op: 'in'; values: string[] }
  assignedTo?: { op: 'in'; values: string[] }
  reviewed?: { op: 'isNotNull' }
}

export const FilterOpIn = z.object({
  op: z.literal('in'),
  values: z.string().array(),
})

export const metricFilters = z.object({
  workspaceIds: FilterOpIn.optional(),
  groupIds: FilterOpIn.optional(),
  channelIds: FilterOpIn.optional(),
  reportToIds: FilterOpIn.optional(),
  handledBy: FilterOpIn.optional(),
  assignedTo: FilterOpIn.optional(),
  scorecardIds: FilterOpIn.optional(),
  reviewed: z
    .object({
      op: z.literal('isNotNull'),
    })
    .optional(),
})

interface Props {
  name: string
  id: string
  scorecardId?: string
  scope?: 'All' | 'Below' | 'Above'
  filters: MetricFilters
}

const DEFAULT_TIMESPAN = {
  start: subWeeks(new Date(), 4),
  end: new Date(),
} as const
